import type { DemoAccount } from '../components/SignInForm';

/**
 * The seeded accounts, offered on the sign-in screens as one-click fill.
 *
 * **Development only, and not merely hidden in production.** Every list below
 * is built behind a literal `import.meta.env.DEV`, which Vite replaces with
 * `false` at build time so the whole array is pruned and the strings never
 * reach a production bundle. That is the point: gating only the *render* left
 * the passwords sitting in `dist/assets/*.js` for anyone to read, which is
 * exactly what CLAUDE.md §9 forbids. Verified by grepping the built file.
 *
 * Write the check as `import.meta.env.DEV` here rather than `env.isDev` from
 * `config/env` — the indirection through an object is opaque to the bundler,
 * and the dead branch survives.
 *
 * They exist so that walking every console on a fresh machine does not mean
 * opening the seed file. Nothing offered here may ever be a live account.
 *
 * One list rather than a copy per screen: two copies is two chances to leave a
 * stale password on one of them — which is exactly what had happened when
 * there were two login pages.
 */
const isDev = import.meta.env.DEV;

const DEMO_PASSWORDS = {
  platform: 'SuperAdmin@2026',
  admin: 'SchoolAdmin@2026',
  teacher: 'Teacher@2026',
  student: 'Student@2026',
  parent: 'Parent@2026',
} as const;

/** Our own staff — the platform console. Offered on `/login` with everybody else. */
export const PLATFORM_DEMO_ACCOUNTS: DemoAccount[] = isDev
  ? [
      {
        role: 'Super admin',
        email: 'superadmin@example.com',
        password: DEMO_PASSWORDS.platform,
      },
    ]
  : [];

/**
 * Everybody inside an institute.
 *
 * There is one login endpoint and the role decides where you land, so all four
 * sign in on this screen and each arrives in their own console.
 */
export const INSTITUTE_DEMO_ACCOUNTS: DemoAccount[] = isDev
  ? [
      { role: 'School admin', email: 'admin@sunrise.example.com', password: DEMO_PASSWORDS.admin },
      { role: 'Teacher', email: 'teacher@sunrise.example.com', password: DEMO_PASSWORDS.teacher },
      { role: 'Student', email: 'student@sunrise.example.com', password: DEMO_PASSWORDS.student },
      { role: 'Parent', email: 'parent@sunrise.example.com', password: DEMO_PASSWORDS.parent },
    ]
  : [];
